/**
 * The landing dashboard's analytics band: freshness line, funnel KPI row, trend chart and the DAU
 * strip (docs/05-admin/05 §3.1 items 1, 2, 3 and the DAU half of 5).
 *
 * **Built from the spec, not a design.** `screens-ops.jsx` `DashboardBody` draws operational tiles
 * only; the owner ruled on 2026-10-26 that design-less screens are built from the docs with the
 * console's own components and restyled later.
 *
 * Everything here reads the nightly `metric_daily` rollup through `GET /v1/admin/analytics/metrics`
 * — two calls, the window and the equal-length window before it, for the deltas. The rollup has
 * no row for today until 02:00 IST tomorrow, so the window ends **yesterday (IST)** and the current
 * partial day is never charted as a dip. None of it is money: revenue lives in the ledger tiles
 * above, and nothing in this file reads or labels a rupee figure (§1 boundary).
 */
import { Button, Kicker } from '../components/primitives';
import { Select } from '../components/forms';
import { Section, Sparkline, StatTile } from '../components/kit';
import { TrendChart } from '../components/charts';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useMetrics, type MetricPoint } from '../lib/api/hooks/analytics';
import { csvField, download, windowDates } from './Funnel';
import { daysBetween, resolveWindow, useWindowSearch } from '../app/urlState';

/** `/v1/admin/analytics/*` is GROWTH-scoped on the server (superadmin passes every gate). */
export const ANALYTICS_SCOPES = ['superadmin', 'growth'] as const;

/** The KPI row, in the doc's order. Keys are the rollup's own (`ROLLUP_METRIC_KEYS`). */
export const KPI_CARDS = [
  { key: 'funnel.puja_viewed', label: 'Puja views', drill: 'funnel' },
  { key: 'funnel.checkout_started', label: 'Checkouts started', drill: 'funnel' },
  { key: 'funnel.payment_succeeded', label: 'Bookings paid', drill: 'funnel' },
  { key: 'funnel.booking_completed', label: 'Completed', drill: 'funnel' },
  { key: 'search.zero_results', label: 'Zero-result searches', drill: 'metrics' },
] as const;

/** The trend chart's default series — the four funnel steps of the KPI row. */
export const TREND_KEYS = KPI_CARDS.slice(0, 4).map((c) => c.key);

const DAU_KEYS = ['dau.mobile', 'dau.web', 'attribution.web_to_app'] as const;

export const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 28, label: 'Last 28 days' },
  { days: 90, label: 'Last 90 days' },
] as const;

/**
 * The window's rollup rows as CSV (doc 05 §3.1, "Export CSV"): one row per day, key and grain,
 * values verbatim. Built from the response on screen — it is the whole window, never paged — so
 * the file cannot disagree with the tiles. Read-only; no audit entry.
 */
export function dashboardCsv(items: readonly MetricPoint[]): string {
  const header = ['metric_date', 'metric_key', 'city_id', 'puja_id', 'value'];
  const rows = items.map((p) =>
    [p.metric_date, p.metric_key, p.city_id, p.puja_id, p.value].map(csvField).join(','),
  );
  return [header.join(','), ...rows].join('\n') + '\n';
}

/** Yesterday in IST — the last day the rollup can have written. */
export function rolledThrough(now = new Date()): Date {
  const ist = new Date(now.getTime() + 330 * 60_000);
  ist.setUTCDate(ist.getUTCDate() - 1);
  return ist;
}

/** The equal-length window that ends the day before `from`. */
export function previousWindow(from: string, days: number): { from: string; to: string } {
  const end = new Date(`${from}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  return windowDates(days, end);
}

export function sumKey(items: readonly MetricPoint[], key: string): number {
  return items.filter((p) => p.metric_key === key).reduce((n, p) => n + p.value, 0);
}

/** One value per day of the window, zero-filled: a day the rollup wrote nothing for is a zero
 * on the line, never a gap that the polyline would silently bridge. */
export function dailySeries(items: readonly MetricPoint[], key: string, from: string, days: number): number[] {
  const byDay = new Map(items.filter((p) => p.metric_key === key).map((p) => [p.metric_date, p.value]));
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(`${from}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    return byDay.get(d.toISOString().slice(0, 10)) ?? 0;
  });
}

/** "+12% vs the previous 7 days"; a previous window of zero has no ratio, so it says so. */
export function deltaNote(current: number, previous: number, days: number): string {
  const span = `the previous ${days} days`;
  if (previous === 0) return current === 0 ? `No change vs ${span}` : `New vs ${span} (was 0)`;
  const pct = Math.round(((current - previous) / previous) * 100);
  return `${pct > 0 ? '+' : ''}${pct}% vs ${span}`;
}

/** Summed integers divided once, as a percentage to one place; `—` when nothing was viewed. */
export function conversion(paid: number, viewed: number): string {
  return viewed === 0 ? '—' : `${((paid / viewed) * 100).toFixed(1)}%`;
}

export function latestDate(items: readonly MetricPoint[]): string | null {
  return items.reduce<string | null>((max, p) => (max === null || p.metric_date > max ? p.metric_date : max), null);
}

const fmt = (n: number) => n.toLocaleString('en-IN');

/** Console tokens only (doc 03 §6.9); the legend chips carry the names, so colour is never the only key. */
const SERIES_TONE = ['var(--c-info)', 'var(--c-saffron)', 'var(--c-success)', 'var(--c-ink2)'];

function FunnelTrend({ items, from, days }: { items: readonly MetricPoint[]; from: string; days: number }) {
  return (
    <Section title="Funnel trend" note={`Per day, ${from} onward`}>
      <TrendChart
        series={TREND_KEYS.map((key, i) => ({
          key,
          label: KPI_CARDS[i]!.label,
          tone: SERIES_TONE[i]!,
          points: dailySeries(items, key, from, days),
        }))}
      />
    </Section>
  );
}

export function DashboardAnalytics({ now }: { now?: Date }) {
  const { go } = useConsole();
  /* The window lives in the URL (doc 05 §2), so a pasted dashboard link reopens the same window. */
  const [search, setSearch] = useWindowSearch();
  const end = rolledThrough(now);
  const current = resolveWindow(
    search,
    WINDOWS.map((w) => w.days),
    7,
    end,
  );
  /* A pasted custom range has no preset, so the comparison window is the same length before it. */
  const days = current.days ?? daysBetween(current.from, current.to);
  const previous = previousWindow(current.from, days);
  const kpiKeys = KPI_CARDS.map((c) => c.key);
  const cur = useMetrics({ date_from: current.from, date_to: current.to }, [...kpiKeys, ...DAU_KEYS]);
  const prev = useMetrics({ date_from: previous.from, date_to: previous.to }, kpiKeys);

  const items = cur.data?.items ?? [];
  const prevItems = prev.data?.items ?? [];
  const through = latestDate(items);
  /* Called only once rows exist, so `through` is set; a key with no row that day is a zero. */
  const latest = (key: string) => items.find((p) => p.metric_key === key && p.metric_date === through)?.value ?? 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      <div style={{ display: 'flex', alignItems: 'end', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Kicker>Growth analytics — behavioural rollups, never the money truth</Kicker>
          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
            {through ? `Data through ${through}` : `Window ${current.from} – ${current.to}`} · nightly rollup 02:00 IST ·{' '}
            <button
              type="button"
              onClick={() => go('audit')}
              style={{ background: 'none', border: 0, padding: 0, color: 'var(--c-info)', cursor: 'pointer', font: 'inherit' }}
            >
              rollup job health
            </button>
          </span>
        </div>
        <Select
          density="admin"
          label="Window"
          options={WINDOWS.map((w) => w.label)}
          value={WINDOWS.find((w) => w.days === current.days)?.label ?? ''}
          onChange={(label) => {
            const w = windowDates(WINDOWS.find((x) => x.label === label)!.days, end);
            setSearch({ date_from: w.from, date_to: w.to });
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          icon="refresh-cw"
          onClick={() => {
            void cur.refetch();
            void prev.refetch();
          }}
        >
          Refresh
        </Button>
        <Button
          size="sm"
          variant="secondary"
          icon="download"
          disabled={items.length === 0}
          onClick={() => download(`bookmypandiit-dashboard-${current.from}-${current.to}.csv`, dashboardCsv(items))}
        >
          Export CSV
        </Button>
      </div>

      {cur.isPending && <LoadingState message="Reading the rollup…" />}
      {cur.isError && <ErrorState title="Could not read the analytics rollup" cause={errorMessage(cur.error)} />}
      {cur.isSuccess && items.length === 0 && (
        <EmptyState
          icon="chart-no-axes-column"
          title="No metrics for this window yet"
          body="The nightly analytics_rollup job fills this dashboard after the first full day."
        />
      )}
      {cur.isSuccess && items.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--sp-md)' }}>
            {KPI_CARDS.map((card) => {
              const value = sumKey(items, card.key);
              return (
                <div key={card.key} style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
                  <StatTile
                    label={card.label}
                    value={fmt(value)}
                    note={prev.isSuccess ? deltaNote(value, sumKey(prevItems, card.key), days) : 'No comparison yet'}
                    onClick={() => go(card.drill)}
                  />
                  <Sparkline points={dailySeries(items, card.key, current.from, days)} />
                </div>
              );
            })}
            <StatTile
              label="View → paid conversion"
              value={conversion(sumKey(items, 'funnel.payment_succeeded'), sumKey(items, 'funnel.puja_viewed'))}
              note="Bookings paid ÷ puja views, this window"
              onClick={() => go('funnel')}
            />
          </div>
          <FunnelTrend items={items} from={current.from} days={days} />
          <Section title="Daily active users" note={`On ${through}`}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--sp-md)' }}>
              <StatTile label="App users" value={fmt(latest('dau.mobile'))} note="dau.mobile" onClick={() => go('metrics')} />
              <StatTile label="Website visitors" value={fmt(latest('dau.web'))} note="dau.web" onClick={() => go('metrics')} />
              <StatTile
                label="Web → app installs"
                value={fmt(sumKey(items, 'attribution.web_to_app'))}
                note="attribution.web_to_app, this window"
                onClick={() => go('metrics')}
              />
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
